import {Lang} from 'yougile-lang'
import {logger, PubSub} from 'yougile-util'
import {
  DataObjectId,
  Hub,
  StickerType,
  Tag,
  TimetrackerBoardSettings,
  TimetrackerValue
} from 'yougile-model'
import {Sync} from '../../../client/sync'
import {Sticker, stickerCtl} from '../sticker'
import {StickerContainerKey, StickerHub} from '../views-web/a-sticker-view'
import {ASystemStickerCtl} from './a-system-sticker-ctl'

export class TimetrackingCtl extends ASystemStickerCtl<TimetrackerValue> {
  public requestedValue:
    | {taskId: DataObjectId; container: StickerContainerKey}
    | undefined
  public completeRequests: Map<
    DataObjectId,
    {container: StickerContainerKey; cb: () => void}
  > = new Map()

  public isInCompletedColumn(task: StickerHub): boolean {
    const box = Sync.get(task)
    const col = box.get(task.location) as Hub
    const board = box.get(col.location) as Hub
    const sticker = this.getSticker(box)
    const conf = board.stickers[sticker.id] as TimetrackerBoardSettings
    return conf?.complete && col.id in conf.complete
  }

  public requestPlan(task: Hub, container: StickerContainerKey): boolean {
    const box = Sync.get(task)
    const col = box.get(task.location) as Hub
    const board = box.get(col.location) as Hub
    const sticker = this.getSticker(box)
    const userSticker = stickerCtl.User.getSticker(box)
    const hasTimetracker = Sticker.isInBoard(board, this.getSticker(box))
    const conf = board.stickers[sticker.id]
    const excluded = conf?.exclude && col.id in conf.exclude
    const val = task.stickers[sticker.id] as TimetrackerValue
    if (hasTimetracker && !excluded && (!val || !val.plan)) {
      this.requestedValue = {taskId: task.id, container}
      if (!Sticker.isPinned(userSticker, task)) {
        Sticker.pin(userSticker, container, task, '')
      }
      return false
    } else {
      return true
    }
  }

  public requestComplete(
    task: Hub,
    container: StickerContainerKey
  ): Promise<boolean> {
    const box = Sync.get(task)
    const col = box.get(task.location) as Hub
    const board = box.get(col.location) as Hub
    const sticker = this.getSticker(box)
    const hasTimetracker = Sticker.isInBoard(board, sticker)
    const conf = board.stickers[sticker.id]
    const newLocal = conf?.exclude && col.id in conf.exclude
    const excluded = newLocal
    const val = task.stickers[sticker.id] as TimetrackerValue

    if (hasTimetracker && !excluded && (!val || !val.plan || !val.work)) {
      return new Promise(resolve => {
        this.completeRequests.set(task.id, {
          container,
          cb: () => {
            const v = task.stickers[sticker.id] as TimetrackerValue
            resolve(v && !!v.plan && !!v.work)
          }
        })
        PubSub.publish(task.id)
      })
    } else {
      return Promise.resolve(true)
    }
  }

  public handleMove(
    task: Hub,
    _: Hub,
    to: Hub,
    container: StickerContainerKey
  ): Promise<boolean> {
    const box = Sync.get(task)
    const toBoard = box.get(to.location) as Hub
    const sticker = this.getSticker(box)
    const hasTimetracker = Sticker.isInBoard(toBoard, sticker)
    if (!hasTimetracker) {
      return Promise.resolve(true)
    }
    const conf = toBoard.stickers[sticker.id] as TimetrackerBoardSettings
    const excluded = conf?.exclude && to.id in conf.exclude
    if (excluded) {
      return Promise.resolve(true)
    }
    const complete = conf?.complete && to.id in conf.complete
    if (complete) {
      logger.log('request complete!')
      return this.requestComplete(task, container)
    } else {
      this.requestPlan(task, container)
      return Promise.resolve(true)
    }
  }

  public isOnTask(task: Hub): boolean {
    const box = Sync.get(task)
    const col = box.get(task.location) as Hub
    const board = box.get(col.location) as Hub
    const timetracking = this.getSticker(box)
    if (!timetracking) {
      return false
    }
    const ttId = timetracking.id
    if (board && ttId in board.stickers) {
      const conf = board.stickers[ttId]
      const excluded = conf?.exclude && col.id in conf.exclude
      if (!excluded) {
        return true
      }
    }
    return false
  }

  public enrichWithTimetrackingIfNeeded(
    task: StickerHub,
    taskStickerIds: DataObjectId[],
    boardId: DataObjectId
  ): void {
    const box = Sync.get(task)
    const col = box.get(task.location) as Hub
    if (!col) {
      return
    }
    const board = box.get(boardId) as Hub
    if (!board) {
      return
    }
    const sticker = this.getSticker(box)
    if (!sticker) {
      return
    }
    const ttId = sticker.id

    if (board && ttId in board.stickers && !taskStickerIds.includes(ttId)) {
      const conf = board.stickers[ttId]
      const excluded = conf?.exclude && col.id in conf.exclude
      if (!excluded) {
        taskStickerIds.push(ttId)
      }
    }
  }

  public override get title(): string {
    return 'Timetracking'
  }

  public get type(): StickerType {
    return 'timetracking'
  }

  public displayedTitle(_: Tag): string {
    return Lang.msg.timetracking
  }

  public static formatValue(value: TimetrackerValue): string {
    const {estimation, spent} = TimetrackingCtl

    return `${spent(value)} / ${estimation(value)}`
  }

  public static estimation(value: TimetrackerValue): string {
    return TimetrackingCtl.h2s(value.plan, '-')
  }

  public static spent(value: TimetrackerValue): string {
    return TimetrackingCtl.h2s(value.work, '-')
  }

  // hours to string interval
  public static h2s = (h: number, defaultValue: string): string => {
    if (!+h) {
      return defaultValue
    }
    const intHours = Math.floor(h)
    const minutes = Math.round((h - intHours) * 60)
    if (intHours === 0) {
      return minutes + ' ' + Lang.msg.m
    }
    return intHours + Lang.msg.h + (minutes > 0 ? minutes : '')
  }
  public static hhmm = (h: number, defaultValue: string): string => {
    if (!+h) {
      return defaultValue
    }
    const intHours = Math.floor(h)
    const hours = intHours < 9 ? `0${intHours}` : intHours

    const intMinutes = Math.round((h - intHours) * 60)
    const minutes = intMinutes < 9 ? `0${intMinutes}` : intMinutes

    return `${hours}:${minutes}`
  }
}
